import { useState, useEffect } from "react";
import { Form } from "react-admin";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import LoadingOverlay from "../../../core/LoadingOverlay";
import DismissibleMessage from "../DismissibleMessage";
import { calibrationService } from "../../../../services/CalibrationService";
import { ClientsDropdown, ProjectsDropdown } from "../DropdownV2";
import { useStyles } from "./styles";
import {
    DEFAULT_GROUP_KEY,
    PLSCADD_KMZ_CONFIGS_DATASET_TYPE,
    PLSCADD_KMZ_DATASET_TYPE,
    PLSCADD_KMZ_REPORT_DATASET_TYPE,
    ROOT_GEOJSON_DATASET_TYPE,
} from "./constants";

// A tap-candidate message repeats each arrive/depart wire many times; collapse
// it to the distinct lines (preserving order) for a readable summary.
function dedupeMessageLines(message) {
    const seen = new Set();
    const lines = [];
    for (const raw of (message || "").split("\n")) {
        const line = raw.trim();
        if (line && !seen.has(line)) {
            seen.add(line);
            lines.push(line);
        }
    }
    return lines;
}

export default function ConfigTab() {
    const classes = useStyles();
    const [clientName, setClientName] = useState("");
    const [projectName, setProjectName] = useState("");
    const [configText, setConfigText] = useState("");
    const [configLoading, setConfigLoading] = useState(false);
    // Whether there's a config to display: a stored one, or a scaffold built from
    // the last run's KMZs. False when no run exists -> the editor is hidden.
    const [configAvailable, setConfigAvailable] = useState(false);
    const [report, setReport] = useState(null); // { data, versionDttm } | null
    const [reportLoading, setReportLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Load the stored config for the selected client + project, or scaffold one
    // from the KMZs fed into the last run. If there's no last run, don't display a
    // config at all.
    useEffect(() => {
        if (!clientName || !projectName) {
            setConfigText("");
            setConfigAvailable(false);
            return;
        }
        let cancelled = false;
        const loadConfig = async () => {
            setConfigLoading(true);
            try {
                let configs = null;
                try {
                    const existing = await calibrationService.getLatestDataset(
                        clientName,
                        projectName,
                        DEFAULT_GROUP_KEY,
                        PLSCADD_KMZ_CONFIGS_DATASET_TYPE,
                        false
                    );
                    configs = existing?.data?.configs ?? null;
                } catch {
                    configs = null; // 404 -> no stored config; scaffold from the last run below
                }
                if (configs !== null && Object.keys(configs).length > 0) {
                    // A stored config exists -> show it as-is.
                    if (!cancelled) {
                        setConfigText(JSON.stringify({ configs }, null, 2));
                        setConfigAvailable(true);
                    }
                    return;
                }

                // No stored config -> scaffold an empty config keyed by the KMZs
                // that were fed into the last run (the report's PlsCaddKmz inputs),
                // with kmz_id pre-filled from the name.
                const report = await calibrationService
                    .getLatestDataset(
                        clientName,
                        projectName,
                        DEFAULT_GROUP_KEY,
                        PLSCADD_KMZ_REPORT_DATASET_TYPE,
                        false
                    )
                    .catch(() => null);
                const kmzNames = [
                    ...new Set(
                        (report?.meta?.input_datasets || [])
                            .filter(
                                (ds) =>
                                    ds.dataset_type === "PlsCaddKmz" ||
                                    ds.dataset_type === PLSCADD_KMZ_DATASET_TYPE
                            )
                            .map((ds) => ds.group_key)
                            .filter(Boolean)
                    ),
                ];
                if (kmzNames.length === 0) {
                    // No last run found -> nothing to scaffold; hide the config.
                    if (!cancelled) {
                        setConfigText("");
                        setConfigAvailable(false);
                    }
                    return;
                }
                const scaffold = kmzNames.reduce((acc, name) => {
                    acc[name] = { kmz_id: name };
                    return acc;
                }, {});
                if (!cancelled) {
                    setConfigText(JSON.stringify({ configs: scaffold }, null, 2));
                    setConfigAvailable(true);
                }
            } catch (err) {
                if (!cancelled) {
                    setErrorMessage(`Failed to load config: ${err}`);
                }
            } finally {
                if (!cancelled) {
                    setConfigLoading(false);
                }
            }
        };
        loadConfig();
        return () => {
            cancelled = true;
        };
    }, [clientName, projectName]);

    // Load the latest run report + its RootGeojson for the selected client + output
    // project. The report supplies the default-conductor lines and tap candidates;
    // the RootGeojson supplies the full list of lines in the run.
    useEffect(() => {
        if (!clientName || !projectName) {
            setReport(null);
            return;
        }
        let cancelled = false;
        const loadReport = async () => {
            setReportLoading(true);
            const [reportResp, rootGeojson] = await Promise.all([
                calibrationService
                    .getLatestDataset(clientName, projectName, DEFAULT_GROUP_KEY, PLSCADD_KMZ_REPORT_DATASET_TYPE, false)
                    .catch(() => null),
                // root-geojson uses a sidecar GeoJSON strategy, so fetch data-only to
                // get the raw FeatureCollection ({ type, features }) rather than the
                // {meta, data} wrapper (whose `data` is only a sidecar reference).
                calibrationService
                    .getLatestDataset(clientName, projectName, DEFAULT_GROUP_KEY, ROOT_GEOJSON_DATASET_TYPE, false, true)
                    .catch(() => null),
            ]);
            if (cancelled) return;
            if (!reportResp && !rootGeojson) {
                setReport(null);
                setReportLoading(false);
                return;
            }
            const lines = (rootGeojson?.features || [])
                .filter((feature) => feature?.properties?.feature_type === "line")
                .map((feature) => feature.properties.line_name)
                .filter(Boolean)
                .sort((a, b) => a.localeCompare(b));
            setReport({
                versionDttm: reportResp?.meta?.version_dttm,
                lines,
                lineDefaultConductor: reportResp?.data?.line_default_conductor || {},
                tapCandidates: reportResp?.data?.tap_candidates || {},
            });
            setReportLoading(false);
        };
        loadReport();
        return () => {
            cancelled = true;
        };
    }, [clientName, projectName]);

    const handleSubmit = async () => {
        setSubmitting(true);
        let parsed;
        try {
            parsed = JSON.parse(configText);
        } catch (err) {
            setErrorMessage(`Config is not valid JSON: ${err}`);
            setSubmitting(false);
            return;
        }
        const configs = parsed?.configs;
        if (!configs || typeof configs !== "object" || Array.isArray(configs)) {
            setErrorMessage('Config must be an object with a "configs" mapping.');
            setSubmitting(false);
            return;
        }
        try {
            await calibrationService.plscaddKmzConfigUpload(clientName, projectName, configs);
            setStatusMessage("Config updated, re-run the conversion process");
        } catch (err) {
            setErrorMessage(`Failed to save config: ${err}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ paddingBottom: "100px" }}>
            <Typography
                variant="body2"
                style={{ textAlign: "center", maxWidth: "640px", margin: "0 auto 20px", color: "#666" }}
            >
                The report reflects the last conversion run for the selected client and project. The
                config is stored per client-project. Re-run the conversion process after changing the config.
            </Typography>
            <Form className={classes.formContainer}>
                <DismissibleMessage message={errorMessage} type="error" onDismiss={() => setErrorMessage(null)} />
                <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
                <LoadingOverlay isLoading={submitting} message="Saving config..." />
                <div className={classes.inputRow}>
                    <ClientsDropdown
                        value={clientName}
                        setValue={setClientName}
                        setError={setErrorMessage}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                    <ProjectsDropdown
                        clientName={clientName}
                        value={projectName}
                        setValue={setProjectName}
                        setError={setErrorMessage}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                </div>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 2,
                        width: "100%",
                        maxWidth: "1200px",
                        marginTop: 2,
                        alignItems: "flex-start",
                    }}
                >
                {clientName && projectName && (
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                        <Typography
                            variant="h6"
                            sx={{
                                textAlign: "center",
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: 1,
                            }}
                        >
                            Conversion Summary
                        </Typography>
                        <Paper sx={{ padding: 1, height: "560px", overflow: "auto" }}>
                        {reportLoading ? (
                            <Typography sx={{ textAlign: "center", fontStyle: "italic" }}>
                                Loading report...
                            </Typography>
                        ) : !report ? (
                            <Typography sx={{ textAlign: "center", fontStyle: "italic", color: "#666" }}>
                                No conversion run found for this client and project yet.
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                    <strong>Last run:</strong> {report.versionDttm || "unknown"}
                                </Typography>

                                <Typography variant="subtitle2" sx={{ marginTop: 1 }}>
                                    Lines in this run ({report.lines.length})
                                </Typography>
                                {report.lines.length === 0 ? (
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "#666" }}>
                                        No RootGeojson found for this run.
                                    </Typography>
                                ) : (
                                    <Box sx={{ whiteSpace: "pre-wrap", color: "#333", fontSize: "0.85rem" }}>
                                        {report.lines.join("\n")}
                                    </Box>
                                )}

                                <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
                                    Lines using a default conductor ({Object.keys(report.lineDefaultConductor).length})
                                </Typography>
                                {Object.keys(report.lineDefaultConductor).length === 0 ? (
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "#666" }}>
                                        None.
                                    </Typography>
                                ) : (
                                    <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                                        {Object.keys(report.lineDefaultConductor)
                                            .sort((a, b) => a.localeCompare(b))
                                            .map((line) => {
                                                const conductors = report.lineDefaultConductor[line];
                                                const label = Array.isArray(conductors)
                                                    ? conductors.join(", ")
                                                    : String(conductors);
                                                return (
                                                    <li key={line}>
                                                        <Typography variant="body2" component="span">
                                                            {line} &rarr; {label}
                                                        </Typography>
                                                    </li>
                                                );
                                            })}
                                    </ul>
                                )}

                                <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
                                    Tap candidates ({Object.keys(report.tapCandidates).length})
                                </Typography>
                                {Object.keys(report.tapCandidates).length === 0 ? (
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "#666" }}>
                                        None.
                                    </Typography>
                                ) : (
                                    Object.keys(report.tapCandidates)
                                        .sort((a, b) => a.localeCompare(b))
                                        .map((structure) => (
                                            <Box key={structure} sx={{ marginTop: 1 }}>
                                                <Typography variant="body2">
                                                    <strong>{structure}</strong>
                                                </Typography>
                                                <Box
                                                    component="pre"
                                                    sx={{
                                                        margin: 0,
                                                        fontFamily: "monospace",
                                                        fontSize: "0.75rem",
                                                        whiteSpace: "pre-wrap",
                                                        color: "#333",
                                                    }}
                                                >
                                                    {dedupeMessageLines(report.tapCandidates[structure]).join("\n")}
                                                </Box>
                                            </Box>
                                        ))
                                )}
                            </>
                        )}
                        </Paper>
                    </Box>
                )}

                {clientName && projectName && (
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                        <Typography
                            variant="h6"
                            sx={{
                                textAlign: "center",
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: 1,
                            }}
                        >
                            Conversion Config
                        </Typography>
                        {!configLoading && !configAvailable ? (
                            <Typography
                                sx={{ textAlign: "center", fontStyle: "italic", color: "#666", marginTop: 2 }}
                            >
                                No conversion run found for this client and project yet. Run a conversion
                                before configuring the process.
                            </Typography>
                        ) : (
                            <TextField
                                multiline
                                fullWidth
                                value={configLoading ? "Loading config..." : configText}
                                onChange={(e) => setConfigText(e.target.value)}
                                disabled={configLoading}
                                inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                                sx={{
                                    "& .MuiInputBase-root": {
                                        height: "560px",
                                        alignItems: "flex-start",
                                    },
                                    "& .MuiInputBase-inputMultiline": {
                                        height: "100% !important",
                                        overflow: "auto !important",
                                        boxSizing: "border-box",
                                    },
                                }}
                            />
                        )}
                    </Box>
                )}
                </Box>

                <div className={classes.buttonRow}>
                    <Button
                        variant="contained"
                        disabled={!clientName || !projectName || configLoading || submitting || !configAvailable}
                        onClick={handleSubmit}
                    >
                        Save Config
                    </Button>
                </div>
            </Form>
        </div>
    );
}
